import { createFileRoute } from "@tanstack/react-router"
import {
  Archive,
  Landmark,
  Mail,
  Sparkles,
  Smartphone,
  CreditCard,
  type LucideIcon,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { breadcrumbJsonLd, buildHead, type JsonLdObject } from "@/core/seo"

import { Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../components/landing"
import { HeroBand, StatRow } from "../components/landing/page-parts"
import { IconTile } from "../components/landing/product-art"
import { FAQ_CHECKED, FAQ_GROUPS, FAQ_REVIEWED } from "../content/pages/faq-items"
import { DATA_COUNTRY } from "../content/pages/trust-facts"

const faqJsonLd = (): JsonLdObject => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_GROUPS.flatMap((g) => g.items).map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
})

export const faqSeo = () =>
  buildHead({
    title: "Frequently asked questions",
    description:
      "Answers on ATO record keeping, privacy, the app and billing for Australian sole traders, with sources and last-reviewed dates on each answer.",
    path: "/faq",
    // FAQPage only here (seo-spec section 5): Google no longer shows the rich result, Bing and AI may read it.
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "FAQ" }]), faqJsonLd()],
  })

export const Route = createFileRoute("/_brand-layout/faq")({
  head: () => {
    const { meta, links, scripts } = faqSeo()
    return { meta, links, scripts }
  },
  component: FaqPage,
})

const STATS = [
  { value: "5 years", label: "ATO record rule", icon: Archive, tone: "success" },
  { value: DATA_COUNTRY, label: "data stored", icon: Landmark, tone: "primary" },
  { value: `${TRIAL_DAYS} days`, label: "free trial", icon: Sparkles, tone: "warning" },
] as const

/** One icon per topic, by group id (presentation only; the copy lives in faq-items). */
const TOPIC_ICONS: Record<string, { icon: LucideIcon; tone: "primary" | "success" | "warning" }> = {
  records: { icon: Archive, tone: "success" },
  privacy: { icon: Landmark, tone: "primary" },
  app: { icon: Smartphone, tone: "primary" },
  billing: { icon: CreditCard, tone: "warning" },
}
const topicIcon = (id: string) => TOPIC_ICONS[id] ?? { icon: Sparkles, tone: "primary" as const }

export function FaqPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "FAQ" }]} />
      <HeroBand className="md:grid-cols-2 md:items-center">
        <div className="grid content-start gap-4">
          <p className="text-muted-foreground text-sm">Last reviewed {FAQ_REVIEWED}</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
            Frequently asked questions
          </h1>
          <StatRow label="Key facts" stats={[...STATS]} />
        </div>
        <nav
          aria-label="Jump to a topic"
          className="border-border bg-card rounded-xl border p-4 shadow-sm"
        >
          <p className="text-muted-foreground mb-2 text-sm font-semibold">Jump to a topic</p>
          <ul className="grid grid-cols-2 gap-1 md:grid-cols-1">
            {FAQ_GROUPS.map((g) => {
              const { icon, tone } = topicIcon(g.id)
              return (
                <li key={g.id}>
                  <a
                    href={`#${g.id}`}
                    className="hover:bg-muted focus-visible:ring-ring flex min-h-12 items-center gap-3 rounded-lg px-2 font-semibold outline-none focus-visible:ring-2 md:min-h-14"
                  >
                    <IconTile icon={icon} tone={tone} />
                    {g.title}
                    <span className="text-muted-foreground ml-auto text-sm font-normal max-md:hidden">
                      {g.items.length} {g.items.length === 1 ? "question" : "questions"}
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      </HeroBand>

      <div className="px-band grid gap-8 py-8">
        {FAQ_GROUPS.map((g) => (
          <section key={g.id} id={g.id} aria-labelledby={`${g.id}-title`} className="scroll-mt-20">
            <h2
              id={`${g.id}-title`}
              className="mb-4 flex items-center gap-3 text-2xl font-semibold"
            >
              <IconTile icon={topicIcon(g.id).icon} tone={topicIcon(g.id).tone} />
              {g.title}
            </h2>
            <FaqAccordion
              items={g.items.map((item) => ({
                question: item.question,
                answer: (
                  <>
                    <p>{item.answer}</p>
                    {item.sources && (
                      <div className="mt-2">
                        <SourceLine sources={item.sources} checked={FAQ_CHECKED} />
                      </div>
                    )}
                  </>
                ),
              }))}
            />
          </section>
        ))}
        <section className="bg-card border-border flex flex-wrap items-center gap-4 rounded-lg border p-6">
          <IconTile icon={Mail} />
          <div className="min-w-48 flex-1">
            <h2 className="text-xl font-semibold">Still stuck?</h2>
            <p className="text-muted-foreground mt-1">Email support and a person replies.</p>
          </div>
          <a
            href={`mailto:${config.core.supportEmail}`}
            className="border-border focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md border px-4 font-medium outline-none focus-visible:ring-2"
          >
            Contact support
          </a>
        </section>
      </div>

      <CtaBand
        title="Ready to try it?"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
